import { IconList } from "@tabler/icons-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CalendarIcon from "@/assets/icons/calendar.svg?react";
import FilterIcon from "@/assets/icons/filter.svg?react";
import { AdvancedFilterTab } from "@/components/ui/chat/AdvancedFilterTab";
import { BasicFilterTab } from "@/components/ui/chat/BasicFilterTab";
import { cn } from "@/lib/utils";
import { VocPeriodFilterType } from "@/models/vocPeriodFilter";
import { initialConversationState, useChatStore } from "@/stores/ui/useChatStore";

export const ChatFilter: React.FC = () => {
  const { t } = useTranslation("voiceek_ui");
  const [activeTab, setActiveTab] = useState<"basic" | "advanced">("basic");
  const filterContainerRef = useRef<HTMLDivElement>(null);
  const { categories, vocPeriodStart, vocPeriodEnd, vocPeriodType } = useChatStore((s) => {
    const id = s.currentConversationId;
    return s.conversationStates[id] ?? initialConversationState;
  });
  const setCurrentConversationState = useChatStore((s) => s.setCurrentConversationState);

  const handleClearFilters = () => {
    setCurrentConversationState({
      categories: [],
      vocPeriodType: VocPeriodFilterType.ALL,
      attributeFilters: {},
      tagIds: [],
    });
  };

  return (
    <div
      ref={filterContainerRef}
      className="relative bg-white rounded-[10px] shadow-[0px_0px_10px_0px_rgba(41,61,114,0.15)] w-full z-20 h-[50vh] flex flex-col"
    >
      {/* ヘッダー */}
      <div className="pt-4 px-4 flex items-center gap-2 shrink-0">
        <div className="w-6 h-6 flex items-center justify-center">
          <FilterIcon className="w-6 h-6 fill-[#333333]" />
        </div>
        <span className="text-[18px] font-semibold text-[#2a2a2a] translate-y-[1px]">
          {t("chat.filter.title")}
        </span>

        <button
          onClick={handleClearFilters}
          className="text-[14px] ml-auto font-medium text-[#6888E1] hover:underline hover:cursor-pointer"
        >
          {t("chat.filter.clear")}
        </button>
      </div>

      <div className="p-4 min-h-0 overflow-hidden flex flex-col">
        {/* タブシステム */}
        <div className="flex gap-[3px] items-center">
          <button
            onClick={() => setActiveTab("basic")}
            className={cn(
              "flex items-center px-4 py-2 rounded-tl-[5px] rounded-tr-[5px] transition-colors border-t border-l border-r border-[#6888e1] hover:cursor-pointer",
              activeTab === "basic"
                ? "bg-[#6888e1]"
                : "bg-white",
            )}
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <CalendarIcon
                className={cn(
                  "w-5 h-5",
                  activeTab === "basic" ? "text-white" : "text-[#6888e1]",
                )}
              />
            </span>
            <span
              className={cn(
                "text-[14px] font-semibold ml-1",
                activeTab === "basic" ? "text-white" : "text-[#6888e1]",
              )}
            >
              {t("chat.filter.tabs.periodAndCategory")}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("advanced")}
            className={cn(
              "flex items-center px-4 py-2 rounded-tl-[5px] rounded-tr-[5px] transition-colors border-t border-l border-r border-[#6888e1] hover:cursor-pointer",
              activeTab === "advanced"
                ? "bg-[#6888e1]"
                : "bg-white",
            )}
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <IconList
                className={cn(
                  activeTab === "advanced" ? "stroke-white" : "stroke-[#6888e1]",
                )}
                stroke={2}
              />
            </span>
            <span
              className={cn(
                "text-[14px] font-semibold ml-1",
                activeTab === "advanced" ? "text-white" : "text-[#6888e1]",
              )}
            >
              {t("chat.filter.tabs.detailedFilter")}
            </span>
          </button>
        </div>
        <div className="h-0 w-full border-b border-[#6888e1]" />

        {/* コンテンツエリア */}
        <div className="pt-4 flex-1 overflow-hidden">
          {activeTab === "basic" && (
            <BasicFilterTab
              filterContainerRef={filterContainerRef}
              categories={categories}
              vocPeriodStart={vocPeriodStart}
              vocPeriodEnd={vocPeriodEnd}
              vocPeriodType={vocPeriodType}
              onUpdateState={setCurrentConversationState}
            />
          )}

          {activeTab === "advanced" && <AdvancedFilterTab />}
        </div>
      </div>
    </div>
  );
};
